import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, Loader2, RotateCcw, Save, Zap, Flame } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const RATE_COLUMNS = [
  { key: 'electricity_current', label: 'Elec. Current ($/kWh)', energy: 'electricity', kind: 'current' },
  { key: 'electricity_offer', label: 'Elec. Offer ($/kWh)', energy: 'electricity', kind: 'offer' },
  { key: 'gas_current', label: 'Gas Current ($/Therm)', energy: 'gas', kind: 'current' },
  { key: 'gas_offer', label: 'Gas Offer ($/Therm)', energy: 'gas', kind: 'offer' },
];

export const RatesManagement = ({ userRole }) => {
  const [rates, setRates] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // { [state]: { electricity_current: '0.12', ... } }
  const [savingState, setSavingState] = useState(null);
  const [message, setMessage] = useState(null);

  const canEdit = userRole === 'admin' || userRole === 'manager';

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/rates`, { withCredentials: true });
      setRates(data.rates);
      setOverrides(data.overrides || []);
      setEdits({});
    } catch (err) {
      console.error('Failed to fetch rates:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const showMessage = (text, isError = false) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 4000);
  };

  const getStates = () => {
    if (!rates) return [];
    const states = new Set([
      ...Object.keys(rates.electricity.current),
      ...Object.keys(rates.gas.current),
    ]);
    return Array.from(states).sort();
  };

  const getValue = (state, col) => {
    const edited = edits[state]?.[col.key];
    if (edited !== undefined) return edited;
    const value = rates?.[col.energy]?.[col.kind]?.[state];
    return value !== undefined ? String(value) : '';
  };

  const setValue = (state, key, value) => {
    setEdits(prev => ({ ...prev, [state]: { ...prev[state], [key]: value } }));
  };

  const hasEdits = (state) => edits[state] && Object.keys(edits[state]).length > 0;
  const isOverridden = (state) => overrides.some(o => o.state === state);

  const saveState = async (state) => {
    const stateEdits = edits[state];
    if (!stateEdits) return;
    const payload = { state };
    for (const [key, value] of Object.entries(stateEdits)) {
      const num = parseFloat(value);
      if (isNaN(num) || num < 0) {
        showMessage(`Invalid value for ${state}: ${value}`, true);
        return;
      }
      payload[key] = num;
    }
    setSavingState(state);
    try {
      await axios.put(`${API}/api/admin/rates`, payload, { withCredentials: true });
      showMessage(`Rates updated for ${state}`);
      await fetchRates();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Failed to update rates', true);
    }
    setSavingState(null);
  };

  const resetState = async (state) => {
    if (!window.confirm(`Reset ${state} rates to defaults?`)) return;
    setSavingState(state);
    try {
      await axios.delete(`${API}/api/admin/rates/${state}`, { withCredentials: true });
      showMessage(`Rates for ${state} reset to defaults`);
      await fetchRates();
    } catch (err) {
      showMessage(err.response?.data?.detail || 'Failed to reset rates', true);
    }
    setSavingState(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  return (
    <div data-testid="rates-management">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <DollarSign size={22} style={{ color: 'var(--teal)' }} />
            Energy Rates
          </h2>
          <p style={{ fontSize: '0.813rem', color: '#9CA3AF', margin: 0 }}>
            {canEdit
              ? 'Rates power the public calculator and Wattson AI. Edits apply instantly.'
              : 'View only — contact an admin or manager to change rates.'}
          </p>
        </div>
        {message && (
          <span data-testid="rates-message" style={{
            fontSize: '0.8rem', fontWeight: 500, padding: '6px 12px', borderRadius: 6,
            background: message.isError ? '#FEE2E2' : '#ECFDF5',
            color: message.isError ? '#DC2626' : '#059669',
          }}>
            {message.text}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', fontSize: '0.75rem', color: '#6B7280' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Zap size={13} style={{ color: '#F59E0B' }} /> Electricity in $/kWh</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Flame size={13} style={{ color: '#EF4444' }} /> Natural gas in $/Therm</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8B5CF6', display: 'inline-block' }} /> Custom override active
        </span>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="rates-table">
          <thead>
            <tr style={{ background: '#FAFBFC' }}>
              <th style={{ padding: '0.625rem 1rem', fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>State</th>
              {RATE_COLUMNS.map(col => (
                <th key={col.key} style={{ padding: '0.625rem 0.5rem', fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>
                  {col.label}
                </th>
              ))}
              {canEdit && <th style={{ borderBottom: '1px solid #E5E7EB' }} />}
            </tr>
          </thead>
          <tbody>
            {getStates().map(state => (
              <tr key={state} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`rates-row-${state}`}>
                <td style={{ padding: '0.5rem 1rem', fontWeight: 600, color: 'var(--navy)', fontSize: '0.875rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {state}
                    {isOverridden(state) && (
                      <span title="Custom override active" style={{ width: 8, height: 8, borderRadius: '50%', background: '#8B5CF6', display: 'inline-block' }} />
                    )}
                  </span>
                </td>
                {RATE_COLUMNS.map(col => {
                  const defaultValue = rates?.[col.energy]?.[col.kind]?.[state];
                  return (
                    <td key={col.key} style={{ padding: '0.375rem 0.5rem' }}>
                      {defaultValue === undefined && edits[state]?.[col.key] === undefined ? (
                        <span style={{ fontSize: '0.8rem', color: '#D1D5DB', paddingLeft: 8 }}>—</span>
                      ) : canEdit ? (
                        <Input
                          type="number"
                          step="0.0001"
                          min="0"
                          value={getValue(state, col)}
                          onChange={(e) => setValue(state, col.key, e.target.value)}
                          data-testid={`rate-input-${state}-${col.key}`}
                          style={{
                            width: 110, height: 32, fontSize: '0.8rem', background: '#fff',
                            border: edits[state]?.[col.key] !== undefined ? '1px solid var(--teal)' : '1px solid #E5E7EB',
                            color: 'var(--navy)',
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: '0.8rem', color: '#4B5563' }}>${defaultValue}</span>
                      )}
                    </td>
                  );
                })}
                {canEdit && (
                  <td style={{ padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'flex-end' }}>
                      {hasEdits(state) && (
                        <Button
                          size="sm"
                          onClick={() => saveState(state)}
                          disabled={savingState === state}
                          className="btn-primary"
                          data-testid={`save-rates-${state}`}
                          style={{ height: 30, fontSize: '0.7rem' }}
                        >
                          {savingState === state ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                          <span style={{ marginLeft: 4 }}>Save</span>
                        </Button>
                      )}
                      {isOverridden(state) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resetState(state)}
                          disabled={savingState === state}
                          data-testid={`reset-rates-${state}`}
                          style={{ height: 30, fontSize: '0.7rem', color: '#6B7280' }}
                        >
                          <RotateCcw size={12} />
                          <span style={{ marginLeft: 4 }}>Reset</span>
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
